import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Route } from "lucide-react";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import {
  tripGuidanceApi,
  type PortalTripFilter,
  type TripSummaryDto
} from "../services/tripGuidanceApi";
import { useDrivers } from "../hooks/useDrivers";

const GUIDANCE_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  Planned: { label: "Planned", variant: "secondary" },
  ActiveReroute: { label: "Reroute Enabled", variant: "default" },
  DestinationOnly: { label: "Destination Only", variant: "outline" },
  Completed: { label: "Completed", variant: "secondary" }
};

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  Dispatched: { label: "Active", variant: "default" },
  Delivered: { label: "Delivered", variant: "secondary" },
  Cancelled: { label: "Cancelled", variant: "destructive" }
};

function formatDate(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" });
}

export function TripsPage() {
  const [selectedDriverId, setSelectedDriverId] = useState<string>("all");
  const [tripFilter, setTripFilter] = useState<PortalTripFilter>("All");
  const { data: drivers = [] } = useDrivers(false);

  const { data: trips = [], isLoading, error } = useQuery({
    queryKey: ["trips", selectedDriverId, tripFilter],
    queryFn: () =>
      tripGuidanceApi.getAllTrips(
        selectedDriverId === "all" ? undefined : selectedDriverId,
        tripFilter
      ),
    staleTime: 30_000
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Route className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Trips</h1>
            <p className="text-gray-500 mt-0.5">All dispatched trips with route guidance.</p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground">Filter trips</span>
        <Select value={tripFilter} onValueChange={(value) => setTripFilter(value as PortalTripFilter)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All trips" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All trips</SelectItem>
            <SelectItem value="Active">Ongoing</SelectItem>
          </SelectContent>
        </Select>
        <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="All drivers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All drivers</SelectItem>
            {drivers
              .filter((d) => d.userId)
              .map((d) => (
                <SelectItem key={d.userId!} value={d.userId!}>
                  {d.fullName}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        {(selectedDriverId !== "all" || tripFilter !== "All") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedDriverId("all");
              setTripFilter("All");
            }}
          >
            Clear
          </Button>
        )}
        <span className="ml-auto text-sm text-muted-foreground">{trips.length} trip{trips.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Table */}
      {isLoading ? (
        <LoadingAnimation />
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-600">Failed to load trips.</p>
        </div>
      ) : trips.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <Route className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-medium text-muted-foreground">No trips found</p>
          <p className="text-sm text-muted-foreground mt-1">
            Trips appear here after a route is assigned to a dispatch record.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dispatch</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Customer(s)</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Stops</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Guidance</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trips.map((trip) => (
                <TripRow key={trip.snapshotId} trip={trip} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function TripRow({ trip }: { trip: TripSummaryDto }) {
  const status = STATUS_BADGE[trip.dispatchStatus] ?? { label: trip.dispatchStatus, variant: "secondary" as const };
  const guidance = GUIDANCE_BADGE[trip.guidanceMode] ?? { label: trip.guidanceMode, variant: "outline" as const };

  return (
    <TableRow>
      <TableCell className="font-mono text-sm font-medium">{trip.dispatchNumber}</TableCell>
      <TableCell className="font-medium">{trip.routeName}</TableCell>
      <TableCell>{trip.driverName ?? "—"}</TableCell>
      <TableCell className="max-w-[180px] truncate">{trip.customerNames.join(", ") || "—"}</TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {formatDate(trip.expectedDeliveryDate ?? trip.dispatchedAt)}
      </TableCell>
      <TableCell className="text-sm">
        {trip.completedStops}/{trip.totalStops}
      </TableCell>
      <TableCell>
        <Badge variant={status.variant}>{status.label}</Badge>
      </TableCell>
      <TableCell>
        <Badge variant={guidance.variant}>{guidance.label}</Badge>
      </TableCell>
      <TableCell className="text-right">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/dispatch/${trip.dispatchRecordId}`}>View</Link>
        </Button>
      </TableCell>
    </TableRow>
  );
}
